import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../contexts/LanguageContext';
import { Teacher, User } from '../types/api';
import { Link, useLocation } from 'react-router-dom';

interface HeaderProps {
  onMenuClick: () => void;
  pendingCounts?: {
    lessonRequests: number;
    oneDayRequests: number;
    contacts: number;
    modifications: number;
  };
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, pendingCounts = {
  lessonRequests: 0,
  oneDayRequests: 0,
  contacts: 0,
  modifications: 0,
} }) => {
  const { auth, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // 스크롤 감지 - 방향에 따라 헤더 표시/숨김
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // 스크롤 방향 감지
      if (currentScrollY < 100) {
        // 페이지 최상단 근처에서는 항상 헤더 표시
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY) {
        // 아래로 스크롤 - 헤더 숨김
        setIsHeaderVisible(false);
      } else {
        // 위로 스크롤 - 헤더 표시
        setIsHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // 로그인된 사용자의 isForeign 값에 따라 언어 자동 설정
  useEffect(() => {
    if (auth.isAuthenticated && auth.userInfo) {

      const isForeign = auth.userType === 'teacher'
        ? (auth.userInfo as Teacher).isForeign
        : (auth.userInfo as User).isForeign || (auth.userInfo as User).isForeigner;

      if (isForeign) {
        setLanguage('ENG');
      } else {
        setLanguage('KOR');
      }
    }
  }, [auth.isAuthenticated, auth.userInfo, auth.userType, setLanguage]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // 로그아웃 오류 무시
    }
  };

  const scrollToTop = () => {
    if (auth.isAuthenticated) {
      // 로그인된 사용자: 사용자 타입에 따라 적절한 대시보드로 이동
      if (auth.userType === 'teacher') {
        window.location.href = '/admin/dashboard';
      } else {
        window.location.href = '/user/dashboard';
      }
    } else {
      // 비회원: 홈으로 이동
      window.location.href = '/';
    }
  };

  const getUserName = () => {
    if (!auth.userInfo) return '';

    if (auth.userType === 'teacher') {
      return (auth.userInfo as Teacher).name;
    } else {
      return (auth.userInfo as User).name;
    }
  };

  const getUserRole = () => {
    if (auth.userType === 'teacher') {
      const teacher = auth.userInfo as Teacher;
      return teacher.role === 'admin' ? t('admin') : t('teacher');
    } else {
      return t('member');
    }
  };

  const getRoleClass = () => {
    if (auth.userType === 'teacher') {
      const teacher = auth.userInfo as Teacher;
      return teacher.role === 'admin' ? 'admin-role' : 'teacher-role';
    }
    return '';
  };

  // 현재 경로가 활성화된 링크인지 확인하는 함수
  const isActiveLink = (path: string) => {
    if (path.startsWith('/#')) {
      // 해시 링크인 경우 현재 경로가 홈페이지이고 해당 해시가 활성화되어 있는지 확인
      return location.pathname === '/' && location.hash === path.substring(1);
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };


  // 로그인된 사용자용 헤더 (웹에서만 표시) - 비회원과 동일한 네이비 디자인
  if (auth.isAuthenticated) {
    return (
      <header className={`header header-guest desktop-only ${!isHeaderVisible ? 'header-hidden' : ''}`}>
        <div className="header-left">
          <h1 className="header-title header-logo" onClick={scrollToTop} style={{ cursor: 'pointer' }}>
            DREAM<span className="logo-accent">DRUMS</span>
          </h1>
        </div>

        <div className="header-center">
          {auth.userType === 'teacher' && (
            <nav className="header-nav">
              <Link to="/admin/lesson-requests" className={`nav-link ${isActiveLink('/admin/lesson-requests') ? 'active' : ''}`}>
                {t('lesson_request_management')}
                {pendingCounts.lessonRequests > 0 && (
                  <span className="pending-badge">{pendingCounts.lessonRequests}</span>
                )}
              </Link>
              <Link to="/admin/oneday-requests" className={`nav-link ${isActiveLink('/admin/oneday-requests') ? 'active' : ''}`}>
                {t('oneday_class_management')}
                {pendingCounts.oneDayRequests > 0 && (
                  <span className="pending-badge">{pendingCounts.oneDayRequests}</span>
                )}
              </Link>
              <Link to="/admin/contacts" className={`nav-link ${isActiveLink('/admin/contacts') ? 'active' : ''}`}>
                {t('contact_management')}
                {pendingCounts.contacts > 0 && (
                  <span className="pending-badge">{pendingCounts.contacts}</span>
                )}
              </Link>
              <Link to="/admin/lesson-modifications" className={`nav-link ${isActiveLink('/admin/lesson-modifications') ? 'active' : ''}`}>
                {t('schedule_change_management')}
                {pendingCounts.modifications > 0 && (
                  <span className="pending-badge">{pendingCounts.modifications}</span>
                )}
              </Link>
              <Link to="/admin/lesson-schedule" className={`nav-link ${isActiveLink('/admin/lesson-schedule') ? 'active' : ''}`}>
                {t('lesson_schedule_management')}
              </Link>
              <Link to="/admin/users" className={`nav-link ${isActiveLink('/admin/users') ? 'active' : ''}`}>
                {t('user_management')}
              </Link>
              <Link to="/admin/practice-booking" className={`nav-link ${isActiveLink('/admin/practice-booking') ? 'active' : ''}`}>
                {t('practice_room_management')}
              </Link>
            </nav>
          )}
          {auth.userType === 'user' && (
            <nav className="header-nav">
              <Link to="/user/lesson-schedule" className={`nav-link ${isActiveLink('/user/lesson-schedule') ? 'active' : ''}`}>
                {t('lesson_schedule_change_request')}
              </Link>
              <Link to="/user/practice-booking" className={`nav-link ${isActiveLink('/user/practice-booking') ? 'active' : ''}`}>
                {t('practice_room_booking')}
              </Link>
              <Link to="/user/change-password" className={`nav-link ${isActiveLink('/user/change-password') ? 'active' : ''}`}>
                {t('change_password')}
              </Link>
            </nav>
          )}
        </div>

        <div className="header-right">
          <div className="header-user">
            <span className="user-greeting">
              {getUserName()}{auth.userType === 'teacher' ? ' ' + t('teacher') : t('honorific')} {t('hello')}!
            </span>
            <span className={`user-role ${getRoleClass()}`}>
              {getUserRole()}
            </span>
            <button className="logout-btn" onClick={handleLogout}>
              {t('logout')}
            </button>
          </div>
        </div>
      </header>
    );
  }

  // 비회원용 헤더 (웹에서만 표시) - 네이비 프로 디자인 (항상 솔리드)
  return (
    <header className={`header header-guest desktop-only ${!isHeaderVisible ? 'header-hidden' : ''}`}>
      <div className="header-left">
        <h1 className="header-title header-logo" onClick={scrollToTop} style={{ cursor: 'pointer' }}>
          DREAM<span className="logo-accent">DRUMS</span>
        </h1>
      </div>

      <div className="header-center">
        <nav className="header-nav">
          <Link to="/lesson-request" className={`nav-link ${isActiveLink('/lesson-request') ? 'active' : ''}`}>
            {t('lesson_request')}
          </Link>
          <Link to="/one-day-class" className={`nav-link ${isActiveLink('/one-day-class') ? 'active' : ''}`}>
            {t('oneday_class')}
          </Link>
          <Link to="/academy-tour" className={`nav-link ${isActiveLink('/academy-tour') ? 'active' : ''}`}>
            {t('academy_tour')}
          </Link>
          <Link to="/instructors" className={`nav-link ${isActiveLink('/instructors') ? 'active' : ''}`}>
            {t('instructors')}
          </Link>
          <Link to="/contact" className={`nav-link ${isActiveLink('/contact') ? 'active' : ''}`}>
            {t('contact_qna')}
          </Link>
        </nav>
      </div>

      <div className="header-right">
        <div className="header-guest-actions">
          <a href="/login" className="btn btn-primary">{t('login')}</a>
          <button
            className="lang-btn"
            onClick={() => setLanguage(language === 'KOR' ? 'ENG' : 'KOR')}
          >
            {language}
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;